/**
 * Human-readable display text extractor.
 *
 * Backend deliberation events sometimes store raw agent JSON output as a string
 * in fields typed as `string` (tool_result_summary, reasoning). This function
 * attempts to parse that JSON and extract a readable message, falling back
 * gracefully for truncated or malformed JSON.
 *
 * Built on top of safeText so it never crashes React (addresses minified
 * React error #31 from objects rendered as children).
 */

import { safeText } from './safeText';

const NARRATIVE_KEYS = [
  'executive_brief',
  'summary',
  'brief',
  'message',
  'text',
  'content',
  'reasoning',
];

/** Decode only \uXXXX escapes (used for plain strings that are not JSON). */
function decodePlainString(s: string): string {
  return s.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
}

/** Decode JSON escape sequences in one single pass: \uXXXX, \n, \", \\. */
function decodeJsonEscapes(s: string): string {
  return s.replace(/\\(u[0-9a-fA-F]{4}|n|"|\\)/g, (match) => {
    switch (match) {
      case '\\n':
        return '\n';
      case '\\"':
        return '"';
      case '\\\\':
        return '\\';
      default:
        // \uXXXX
        return String.fromCharCode(parseInt(match.slice(2), 16));
    }
  });
}

/**
 * Extract a string value for `key` from raw JSON text, tolerating truncation.
 * Tries a strict "closed quote" match first, then a best-effort open-ended match.
 * JSON strings are always "-delimited; ' is never a JSON quote character.
 */
function regexExtractString(jsonText: string, key: string): string | null {
  // Strict: closing quote is present.
  const strict = new RegExp(
    `"${key}"\\s*:\\s*"((?:[^"\\\\\\\\]|\\\\\\\\.)*)"`,
    'i',
  );
  const strictMatch = jsonText.match(strict);
  if (strictMatch) {
    const decoded = decodeJsonEscapes(strictMatch[1]);
    return decoded.trim() || null;
  }

  // Forgiving: no closing quote required (truncated JSON).
  const loose = new RegExp(
    `"${key}"\\s*:\\s*"([^"]*)(?:"|$)`,
    'i',
  );
  const looseMatch = jsonText.match(loose);
  if (looseMatch) {
    const decoded = decodeJsonEscapes(looseMatch[1]);
    return decoded.trim() || null;
  }

  return null;
}

/**
 * Compact rendering of top-level scalar fields from a plain object.
 * Strings, numbers, and booleans, max 6 entries, joined by " · ".
 * Falls back to a key-count marker when no scalars are present.
 */
function compactObjectRender(obj: Record<string, unknown>): string | null {
  const entries = Object.entries(obj)
    .filter(([, val]) =>
      typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean',
    )
    .slice(0, 6);
  if (entries.length > 0) {
    return entries.map(([k, val]) => `${k}: ${val}`).join(' · ');
  }
  const keys = Object.keys(obj);
  if (keys.length > 0) {
    return `[object with ${keys.length} keys]`;
  }
  return null;
}

/**
 * Coerce a backend field to human-readable text.
 *
 * - Starts with `{` or `[` → attempt JSON.parse.
 *   - Object → first narrative key with a non-empty string value, else compact
 *     scalar render, else null.
 *   - Array of objects all carrying `item_id` → `${n} items`.
 *   - Otherwise → null.
 * - JSON.parse fails → regex-extract narrative key, else strip JSON punctuation
 *   and decode escapes.
 * - Plain string → decode literal \uXXXX escapes and return.
 * - Non-string inputs handled by safeText first.
 */
export function displayText(v: unknown): string | null {
  const st = safeText(v);
  if (st == null) return null;

  const trimmed = st.trim();
  if (trimmed.length === 0) return null;

  // Plain string that does not look like JSON — just decode escapes.
  // Arrays starting with a bracketed date (e.g. [2026-10-24]) are prose, not JSON.
  const looksLikeJson =
    /^\{\s*"/.test(trimmed) ||
    (/^\[\s*[{"\d\]]/.test(trimmed) && !/^\[\d{4}-\d{2}-\d{2}\]/.test(trimmed));
  if (!looksLikeJson) {
    const decoded = decodePlainString(trimmed);
    return decoded || null;
  }

  // Looks like JSON — try to parse.
  try {
    const parsed = JSON.parse(trimmed);

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const key of NARRATIVE_KEYS) {
        const val = parsed[key];
        if (typeof val === 'string' && val.trim()) {
          return val.trim();
        }
      }
      return compactObjectRender(parsed);
    }

    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return null;

      if (
        parsed.every(
          (item) => item && typeof item === 'object' && 'item_id' in item,
        )
      ) {
        return `${parsed.length} items`;
      }

      if (parsed.every((item) => typeof item === 'string')) {
        return parsed.join(' · ');
      }

      return `${parsed.length} items`;
    }

    return null;
  } catch {
    // Parsing failed — truncated or malformed JSON.
  }

  // Fallback 1: regex-extract a known narrative key.
  for (const key of NARRATIVE_KEYS.slice(0, 2)) {
    const extracted = regexExtractString(trimmed, key);
    if (extracted != null) return extracted;
  }

  // Fallback 2: strip JSON punctuation (never commas or apostrophes),
  // collapse whitespace, decode escapes.
  const cleaned = trimmed
    .replace(/[{}[\]"]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const decoded = decodeJsonEscapes(cleaned);
  return decoded || null;
}
